import LegalLayout, { H2, P, UL } from "@/components/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      description="How MeasureTheRoof collects, uses, and protects your personal information, including your rights under the California Privacy Rights Act (CPRA)."
      path="/privacy"
      updated="June 17, 2026"
    >
      <P>
        This Privacy Policy explains how MeasureTheRoof ("we," "us," or "our") collects, uses,
        discloses, and protects personal information when you use our website and services (the
        "Service"). We are based in and serve customers within the United States. By using the
        Service, you agree to this Policy.
      </P>

      <H2>1. Information We Collect</H2>
      <UL>
        <li><strong>Account information:</strong> name, company name, email address, and password (stored only as a secure hash).</li>
        <li><strong>Order information:</strong> property addresses you submit, the reports you order, and report measurement data.</li>
        <li><strong>Payment information:</strong> processed by our payment provider, Stripe. We do not store your full card number; we retain transaction records (amount, date, status).</li>
        <li><strong>Technical information:</strong> IP address, device/browser type, and usage data collected through cookies and similar technologies (subject to your consent choices).</li>
      </UL>

      <H2>2. How We Use Your Information</H2>
      <UL>
        <li>To provide, operate, and improve the Service, including generating and delivering roof measurement reports.</li>
        <li>To process payments and send receipts and service notifications (e.g., when a report is ready).</li>
        <li>To authenticate you and secure your account.</li>
        <li>To comply with legal obligations and enforce our Terms of Service.</li>
        <li>With your consent, to analyze usage and improve our product.</li>
      </UL>

      <H2>3. How We Share Information</H2>
      <P>We do not sell your personal information for money. We share information only with:</P>
      <UL>
        <li><strong>Service providers</strong> that help us operate the Service (e.g., our aerial imagery and measurement provider, payment processor Stripe, email provider, and hosting/analytics providers), under contractual confidentiality obligations.</li>
        <li><strong>Legal and safety</strong> recipients when required by law or to protect rights, property, or safety.</li>
        <li><strong>Business transfers</strong> in connection with a merger, acquisition, or sale of assets.</li>
      </UL>

      <H2>4. Cookies & Tracking</H2>
      <P>
        We use strictly necessary cookies to run the Service and, with your consent, functional and
        analytics cookies. You can manage your choices at any time through our cookie banner or the
        "Do Not Sell or Share My Personal Information" link in the footer. See our{" "}
        <a href="/cookie-policy" className="text-orange font-semibold">Cookie Policy</a> for details.
      </P>

      <H2>5. Your California Privacy Rights (CPRA/CCPA)</H2>
      <P>
        If you are a California resident, the California Consumer Privacy Act, as amended by the
        California Privacy Rights Act (CPRA), gives you the following rights:
      </P>
      <UL>
        <li><strong>Right to know</strong> what personal information we collect, use, and disclose.</li>
        <li><strong>Right to access and portability</strong> of your personal information.</li>
        <li><strong>Right to correct</strong> inaccurate personal information.</li>
        <li><strong>Right to delete</strong> personal information, subject to legal exceptions.</li>
        <li><strong>Right to opt out</strong> of the "sale" or "sharing" of personal information (including cross-context behavioral advertising). Use the "Do Not Sell or Share My Personal Information" link in our footer.</li>
        <li><strong>Right to limit</strong> the use of sensitive personal information.</li>
        <li><strong>Right to non-discrimination</strong> for exercising your privacy rights.</li>
      </UL>
      <P>
        To exercise any of these rights, email{" "}
        <a href="mailto:privacy@measuretheroof.com" className="text-orange font-semibold">privacy@measuretheroof.com</a>.
        We will verify your request and respond within the timeframes required by law. You may use an
        authorized agent to submit a request on your behalf.
      </P>

      <H2>6. Data Retention</H2>
      <P>
        We retain personal information for as long as your account is active or as needed to provide
        the Service, comply with legal obligations, resolve disputes, and enforce agreements. You may
        request deletion as described above.
      </P>

      <H2>7. Security</H2>
      <P>
        We use administrative, technical, and physical safeguards designed to protect your
        information, including encryption in transit (HTTPS), hashed passwords, and restricted access
        to sensitive credentials. No method of transmission or storage is 100% secure.
      </P>

      <H2>8. Children's Privacy</H2>
      <P>The Service is intended for business users and is not directed to children under 16. We do not knowingly collect personal information from children.</P>

      <H2>9. Changes to This Policy</H2>
      <P>We may update this Policy from time to time. We will post the updated version with a new "Last updated" date and, where appropriate, notify you.</P>

      <H2>10. Contact Us</H2>
      <P>
        Questions about this Policy or your data? Contact{" "}
        <a href="mailto:privacy@measuretheroof.com" className="text-orange font-semibold">privacy@measuretheroof.com</a>.
      </P>
    </LegalLayout>
  );
}
